import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { getNestedGroupsWithIndentation } from "@dashboard/groups/utils";
import { Box, Typography } from "@mui/material";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useUpdateWorkspaceMutation } from "../../api";
import type { Workspace } from "../../types";

import { useStyles } from "./WorkspaceConfigurationPanel.style";

type DefaultGroupRowProps = {
    workspace: Workspace;
};

/**
 * The only place outside the group side panel's enrollment tab where the workspace's default group can be
 * read or changed — tenants that hide that tab would otherwise have to open every group in turn to find it.
 * Deliberately not disabled when the Groups page is switched out of the navigation: the default group still
 * decides where account-credential registrations land, and this would be the last way left to change it.
 */
export function DefaultGroupRow(props: DefaultGroupRowProps) {
    const { workspace } = props;
    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);
    const inputId = useId();

    const groups = useSelector(selectGroups);
    const options = useMemo(() => getNestedGroupsWithIndentation(groups), [groups]);
    const selectedOption = options.find((option) => option.value === workspace.defaultGroupId) ?? null;

    const [updateWorkspace] = useUpdateWorkspaceMutation();
    const [isSaving, setIsSaving] = useState(false);

    const saveDefaultGroup = async (defaultGroupId: number) => {
        setIsSaving(true);
        try {
            await updateWorkspace({ ...workspace, defaultGroupId }).unwrap();
            showSuccessToast(t("pages:workspacePage.toast.workspaceSettingUpdated"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:workspacePage.toast.workspaceSettingUpdateFailed"));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box className={classes.selectRow}>
            <Box className={classes.selectLabel}>
                <Typography variant="body1" color="text.primary" component="label" htmlFor={inputId}>
                    {t("pages:workspacePage.configuration.defaultGroup.label")}
                </Typography>
            </Box>
            <SelectField
                id={inputId}
                className={classes.selectControl}
                options={options}
                value={selectedOption}
                disableSorting
                disabled={isSaving}
                loading={isSaving}
                onChange={(_event, option) => {
                    if (option && typeof option.value === "number" && option.value !== workspace.defaultGroupId) {
                        void saveDefaultGroup(option.value);
                    }
                }}
            />
        </Box>
    );
}
